import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useConvexMutation } from './use-convex-mutation';

// ════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

interface CreateInvoiceData {
  parentUserId: string;
  amountCents: number;
  currency?: string;
  dueDate: string;
  description?: string;
  lineItems?: LineItem[];
}

interface UpdateInvoiceData {
  status?: string;
  amountCents?: number;
  dueDate?: string;
  description?: string;
  lineItems?: LineItem[];
}

type InvoiceStatus = "draft" | "sent" | "paid" | "overdue" | "cancelled";

// ════════════════════════════════════════════════════════════
// DIRECTOR HOOKS
// ════════════════════════════════════════════════════════════

export function useDirectorInvoices(status?: string) {
  const data = useQuery(api.director.invoices.list, { status: status as InvoiceStatus | undefined });
  return { data, isLoading: data === undefined, error: null };
}

export function useInvoiceDetail(invoiceId: string) {
  const data = useQuery(
    api.director.invoices.getDetail,
    invoiceId ? { invoiceId: invoiceId as Id<"invoices"> } : 'skip'
  );
  return { data, isLoading: data === undefined, error: null };
}

export function useCreateInvoice() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.invoices.create);
  return {
    isPending,
    mutateAsync: (data: CreateInvoiceData) => mutateAsync({
      parentUserId: data.parentUserId as Id<"users">,
      amountCents: data.amountCents,
      currency: data.currency,
      dueDate: data.dueDate,
      description: data.description,
      lineItems: data.lineItems,
    }),
  };
}

export function useUpdateInvoice() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.invoices.update);
  return {
    isPending,
    mutateAsync: (args: UpdateInvoiceData & { id: string }) => {
      const { id, status, ...rest } = args;
      return mutateAsync({ invoiceId: id as Id<"invoices">, status: status as InvoiceStatus | undefined, ...rest });
    },
  };
}

export function useDeleteInvoice() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.invoices.remove);
  return {
    isPending,
    mutateAsync: (id: string) => mutateAsync({ invoiceId: id as Id<"invoices"> }),
  };
}

// ════════════════════════════════════════════════════════════
// BULK INVOICE HOOKS
// ════════════════════════════════════════════════════════════

export function useBulkInvoicePreview(
  args: { termId: string; skipUnpaid: boolean } | 'skip'
) {
  const data = useQuery(
    api.director.invoices.bulkPreview,
    args === 'skip' ? 'skip' : { termId: args.termId as Id<"terms">, skipUnpaid: args.skipUnpaid }
  );
  return { data, isLoading: data === undefined };
}

export function useBulkCreateInvoices() {
  const { mutateAsync, isPending } = useConvexMutation(api.director.invoices.bulkCreate);
  return {
    isPending,
    mutateAsync: (args: {
      termId: string;
      currency?: string;
      status?: 'draft' | 'sent';
      dueDate: string;
      description?: string;
      parentUserIds: string[];
      skipUnpaid?: boolean;
    }) => mutateAsync({
      ...args,
      termId: args.termId as Id<"terms">,
      parentUserIds: args.parentUserIds as Id<"users">[],
    }),
  };
}

// ════════════════════════════════════════════════════════════
// PARENT HOOKS
// ════════════════════════════════════════════════════════════

export function useParentInvoices() {
  const data = useQuery(api.parent.invoices.list);
  return { data, isLoading: data === undefined, error: null };
}
